import { Box, Typography, IconButton, TextField, InputAdornment, CircularProgress, Tooltip } from "@mui/material";
import { Colors } from "../../Colors";
import { HeaderBox } from "../../subcomponents/SidebarComponents";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { getArchivedPatients, unarchivePatient } from "../../slices/PatientSlice";
import { convertE164ToReadable } from "../../utils/utils";
import LoadingWithMessage from "../../subcomponents/LoadingWithMessage";

const EmptyState: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => (
  <Box sx={{ textAlign: "center", py: 10 }}>
    <Typography sx={{ color: "#FFF", fontSize: "0.9rem", fontWeight: 500, mb: 0.5 }}>{title}</Typography>
    <Typography sx={{ color: Colors.info, fontSize: "0.813rem" }}>{subtitle}</Typography>
  </Box>
);

export const ArchivedPatientsModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const archivedPatients = useSelector((state: RootState) => state.patients.archivedPatients);
  const loadingFetchArchivedPatients = useSelector((state: RootState) => state.patients.loadingFetchArchivedPatients);
  const loadingUnarchivePatient = useSelector((state: RootState) => state.patients.loadingUnarchivePatient);

  const [processingPatientId, setProcessingPatientId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    if (user?.token) {
      dispatch(getArchivedPatients({ token: user.token }));
    }
  }, [user?.token]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredPatients = useMemo(() => {
    if (!debouncedSearch) return archivedPatients;
    const query = debouncedSearch.toLowerCase().replace(/[^a-z0-9]/g, "");
    return archivedPatients.filter((p) => {
      const ehrId = p.webpt?.webptPatientId ?? "";
      const haystack = `${p.patient.firstName}${p.patient.lastName}${p.patient.phoneNumber}${ehrId}`
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
      return haystack.includes(query);
    });
  }, [archivedPatients, debouncedSearch]);

  const handleUnarchive = async (patientId: string) => {
    if (!user?.token) return;
    setProcessingPatientId(patientId);
    await dispatch(unarchivePatient({ token: user.token, patientId }));
    setProcessingPatientId(null);
  };

  const totalCount = archivedPatients.length;
  const filteredCount = filteredPatients.length;
  const hasPatients = totalCount > 0;
  const isFiltering = debouncedSearch.length > 0;

  return (
    <Box sx={{ overflowY: "auto", height: "92%", paddingBottom: "40px", paddingRight: "20px" }}>
      <HeaderBox title="Archived Patients" onClose={onClose} />

      <Box sx={{ pt: "60px" }}>
        <TextField
          placeholder="Search archived patients"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          fullWidth
          disabled={loadingFetchArchivedPatients || !hasPatients}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: Colors.info, fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery("")} sx={{ color: Colors.info, p: 0.25 }}>
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: Colors.textfield,
              color: "#FFF",
              fontSize: "0.875rem",
              borderRadius: "10px",
            },
          }}
        />

        {loadingFetchArchivedPatients ? (
          <LoadingWithMessage message="Loading..." size={20} customStyles={{ marginTop: 32 }} />
        ) : !hasPatients ? (
          <EmptyState title="No archived patients" subtitle="Patients you archive will appear here." />
        ) : filteredCount === 0 ? (
          <EmptyState title="No matches" subtitle={`Nothing matches "${debouncedSearch}".`} />
        ) : (
          <>
            <Typography sx={{ color: Colors.info, fontSize: "0.75rem", mt: 3, mb: 0.5 }}>
              {isFiltering ? `${filteredCount} of ${totalCount}` : `${totalCount} ${totalCount === 1 ? "patient" : "patients"}`}
            </Typography>

            <Box>
              {filteredPatients.map((patient, idx) => {
                const ehrId = patient.webpt?.webptPatientId;
                const isProcessing = processingPatientId === patient.patientId;
                const phone = convertE164ToReadable(patient.patient.phoneNumber);
                const meta = [phone, ehrId ? `ID ${ehrId}` : null].filter(Boolean).join("  ·  ");
                const isLast = idx === filteredPatients.length - 1;

                return (
                  <Box
                    key={patient.patientId}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      py: 1.75,
                      borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.06)",
                      "&:hover .restore-btn": { opacity: 1, color: Colors.primary },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          color: "#FFF",
                          fontSize: "0.9rem",
                          fontWeight: 500,
                          lineHeight: 1.3,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {patient.patient.firstName} {patient.patient.lastName}
                      </Typography>
                      <Typography
                        sx={{
                          color: Colors.info,
                          fontSize: "0.75rem",
                          mt: 0.5,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {meta || "—"}
                      </Typography>
                    </Box>

                    <Tooltip title="Restore" arrow placement="left">
                      <span>
                        <IconButton
                          className="restore-btn"
                          onClick={() => handleUnarchive(patient.patientId)}
                          disabled={loadingUnarchivePatient || isProcessing}
                          size="small"
                          sx={{
                            color: Colors.info,
                            opacity: isProcessing ? 1 : 0.55,
                            transition: "opacity 120ms, color 120ms",
                            "&:hover": { backgroundColor: "transparent" },
                            "&.Mui-disabled": { color: Colors.info },
                          }}
                        >
                          {isProcessing ? (
                            <CircularProgress size={16} sx={{ color: Colors.primary }} />
                          ) : (
                            <AddIcon sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
